import {
	ContactControllerApiFactory,
	type ContactRequest,
} from '@api-client/api'
import { AsyncButton } from '@components/AsyncButton'
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAsyncApi } from '../../hooks/useAsyncApi'

interface ContactUsModalProps {
	open: boolean
	onClose: () => void
}

const ContactUsModal = ({ open, onClose }: ContactUsModalProps) => {
	const { t } = useTranslation()
	const { enqueueSnackbar } = useSnackbar()
	const contactApi = useAsyncApi(ContactControllerApiFactory)
	const [message, setMessage] = useState('')

	const handleClose = useCallback(() => {
		setMessage('')
		onClose()
	}, [onClose])

	const handleMessageChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) =>
			setMessage(event.target.value),
		[]
	)

	const handleSubmit = useCallback(async () => {
		const contactRequest: ContactRequest = { message }
		try {
			await contactApi(api => api.submitContactRequest(contactRequest))
			enqueueSnackbar(t('contactUs.successMessage'), { variant: 'success' })
			handleClose()
		} catch {
			enqueueSnackbar(t('toast.error.general'), { variant: 'error' })
		}
	}, [contactApi, enqueueSnackbar, handleClose, message, t])

	const isDisabled = message.trim() === ''

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			aria-labelledby="contact-us-dialog-title"
		>
			<DialogTitle id="contact-us-dialog-title">
				{t('contactUs.title')}
			</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary" mb={2}>
					{t('contactUs.messageLabel')}
				</Typography>
				<TextField
					multiline
					rows={5}
					fullWidth
					placeholder={t('contactUs.messagePlaceholder')}
					value={message}
					onChange={handleMessageChange}
				/>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={handleClose}>
					{t('button.cancel')}
				</Button>
				<AsyncButton
					variant="contained"
					disabled={isDisabled}
					onClick={handleSubmit}
				>
					{t('button.send')}
				</AsyncButton>
			</DialogActions>
		</Dialog>
	)
}

export { ContactUsModal }
